import React, { useState, useEffect, useCallback, useRef } from "react";
import { format } from "date-fns";
import { fr, enUS, arSA } from "date-fns/locale";
import {
  Card,
  CardContent,
  Avatar,
  Typography,
  Box,
  Grid,
  Chip,
  List,
  Button,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Snackbar,
  Collapse,
  Paper,
  Tooltip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  useMediaQuery,
} from "@mui/material";

import {
  Download as DownloadIcon,
  Email as EmailIcon,
  LocationOn as LocationOnIcon,
  School as SchoolIcon,
  Work as WorkIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  History as HistoryIcon,
  InfoOutlined as InfoOutlinedIcon,
  Checklist,
  Language,
  Book as BookIcon,
  WorkspacePremium,
  ViewList as ViewListIcon,
  ViewModule as ViewModuleIcon,
  LinkedIn as LinkedInIcon,
  WorkHistoryOutlined,
  PictureAsPdf as PdfIcon,
  CalendarToday as CalendarTodayIcon,
  Close,
} from "@mui/icons-material";
import { GraduationCap } from "lucide-react";

import DynamicFormSearchBar from "./DynamicFormSearchBar";

import CircularProgress from "@mui/material/CircularProgress";
import { styled } from "@mui/material/styles";
import axios from "axios";
import { useTranslation } from "react-i18next";
import SendEmailModal from "../Emailing/SendEmailModal";
import { useTheme } from "@mui/material/styles";
import EmailHistory from "../Emailing/EmailHistory";
import CustomPagination from "views/client/pagination";
import ApplicationTable2 from "./ApplicationsTable2";
import ApplicationSearchBar from "./ApplicationSearchBar";
import { OutlinedButton } from "../../components/Buttons/Buttons";
import PostAssignmentDialog from "./PostAssignmentDialog";
import { usePostList } from "views/Posts/hooks/usePostList";
import { usePostListForSpontaneous } from "views/Posts/hooks/usePostListForSpontaneous";
import { MdAssignmentAdd } from "react-icons/md";
import { DateCalendar } from "@mui/x-date-pickers";
import { DateComponent } from "@fullcalendar/core/internal";

// Styled Components
const StyledCard = styled(Card)(({ theme }) => ({
  width: "100%",
  borderRadius: "24px",
  marginBottom: theme.spacing(4),
  overflow: "hidden",
  position: "relative",
  background: "#ffffff",
  border: "1px solid rgba(0, 23, 231, 0.06)",
}));

// Blue theme InfoChip
const InfoChip = styled(Chip)(({ theme }) => ({
  margin: "2px",
  fontWeight: 500,
  borderRadius: "12px",
  backgroundColor: "rgba(0, 23, 231, 0.08)",
  color: "#0017E7",
  border: "1px solid rgba(0, 23, 231, 0.15)",
  "& .MuiChip-label": {
    padding: "8px 14px",
    fontSize: "1rem",
    fontWeight: 500,
    whiteSpace: "normal",
    wordBreak: "break-word",
  },
  "& .MuiChip-icon": {
    color: "#0017E7",
  },
}));

// Blue theme ActionButton
const ActionButton = styled(Button)(({ theme }) => ({
  borderRadius: "14px",
  padding: theme.spacing(1.5, 3),
  fontWeight: 600,
  textTransform: "none",
  color: "#0017E7",
  backgroundColor: "transparent",
  border: "1px solid rgba(0, 23, 231, 0.2)",
}));

const SectionTitle = styled(Typography)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  marginBottom: theme.spacing(2),
  fontWeight: 600,
  fontSize: "1.1rem",
  "& svg": {
    marginRight: theme.spacing(1),
  },
}));

const ShowMoreButton = styled(Button)(({ theme }) => ({
  marginTop: theme.spacing(1),
  fontSize: "0.875rem",
  textTransform: "none !important",
  color: "#0017E7",
  fontWeight: 500,
  px: 1.5,
  py: 0.5,
  borderRadius: "8px",
  backgroundColor: "transparent",
}));

// Blue theme for inline show more
const showMoreInlineSx = {
  textTransform: "none !important",
  color: "#0017E7",
  fontWeight: 500,
  fontSize: "0.875rem",
  px: 1.5,
  py: 0.5,
  borderRadius: "8px",
  backgroundColor: "transparent",
  "&:hover": {
    backgroundColor: "transparent",
    color: "#0017E7",
  },
};

// Add the missing truncateLabel function
const truncateLabel = (label, maxLength = 5) => {
  if (typeof label !== "string") return label;
  return label.length > maxLength ? label.slice(0, maxLength) + "..." : label;
};

const apiUrl = process.env.REACT_APP_API_URL;

// Helper function to parse JWT token
const parseJwt = (token) => {
  try {
    const base64Url = token.split(".")[1];
    const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split("")
        .map((c) => "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2))
        .join("")
    );
    return JSON.parse(jsonPayload);
  } catch (error) {
    console.error("Failed to decode JWT:", error);
    return {};
  }
};

// Helper function to get user ID from access token
const getUserIdFromAccessToken = () => {
  const accessToken = localStorage.getItem("accessToken");
  if (!accessToken) {
    console.error("No access token found in local storage");
    return null;
  }
  try {
    const decodedToken = parseJwt(accessToken);
    return decodedToken.user_id;
  } catch (error) {
    console.error("Failed to decode access token", error);
    return null;
  }
};

const ApplicationCard = ({
  application,
  company,
  userId,
  formStructure,
  setRefreshcandidates,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isTablet = useMediaQuery(theme.breakpoints.down("md"));
  const [templates, setTemplates] = useState([]);
  const [isTemplateModalOpen, setIsTemplateModalOpen] = useState(false);
  const [loadingTemplates, setLoadingTemplates] = useState(false);
  const [isEmailHistoryOpen, setIsEmailHistoryOpen] = useState(false);
  const [showAdditionalFields, setShowAdditionalFields] = useState(false);
  const [moreItemsDialogOpen, setMoreItemsDialogOpen] = useState(false);
  const [moreItemsDialogTitle, setMoreItemsDialogTitle] = useState("");
  const [moreItemsDialogContent, setMoreItemsDialogContent] = useState([]);
  // Remove category names state since it's now included in the response
  const { t, i18n } = useTranslation();

  // Add cleanString function
  const cleanString = (str) => {
    if (!str) return "";
    return str.replace(/^\[|\]$|'|"/g, "").trim();
  };

  // Category names are now included in the application response as category_info
  console.log("Application data:", application);
  console.log("Category info:", application.category_info);

  // Extract dynamic fields (those starting with "field_")
  const dynamicFields = Object.keys(application.form_data)
    .filter((key) => key.startsWith("field_"))
    .map((key) => {
      // Find field label from form structure
      const fieldDef = formStructure?.find((field) => field.name === key);
      return {
        name: key,
        value: application.form_data[key],
        label: fieldDef?.label || key.replace("field_", "Field "), // Use the label from form structure
      };
    });

  const processCV = (cv) => {
    if (!cv) return "";
    // Remove '/media/' prefix if present
    if (typeof cv === "string" && cv.startsWith("/media/")) {
      return cv.replace(/^\/media\//, "");
    }
    return cv;
  };

  const handleEmailHistoryClick = () => {
    setIsEmailHistoryOpen(true);
  };

  const fetchTemplates = async () => {
    try {
      setLoadingTemplates(true);
      const recruiterId = userId;
      const response = await axios.get(
        `${apiUrl}recruiters/${recruiterId}/templates/`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("accessToken")}`,
          },
        }
      );

      if (response.data.results) {
        setTemplates(response.data.results);
      } else {
        setTemplates(response.data);
      }
    } catch (error) {
      console.error("Error fetching templates:", error);
    } finally {
      setLoadingTemplates(false);
    }
  };

  const handleResumeClick = async () => {
    if (!application.cv) {
      alert(
        t("dynamicFormTable.resumeNotAvailable", "Resume is not available")
      );
      return;
    }

    const normalizedResumePath = application.cv.replace(/\\/g, "/");
    const fileURL = `${apiUrl}${normalizedResumePath}`;

    try {
      const response = await fetch(`${apiUrl}ai/search_resume/`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ resume_url: fileURL }),
      });

      const contentType = response.headers.get("content-type");

      if (response.ok) {
        if (contentType && contentType.includes("application/pdf")) {
          const blob = await response.blob();
          const url = window.URL.createObjectURL(blob);
          window.open(url, "_blank");
        } else {
          console.log("Unexpected response from server");
        }
      } else {
        if (contentType && contentType.includes("text/html")) {
          const htmlContent = await response.text();
          const newWindow = window.open();
          newWindow.document.write(htmlContent);
          newWindow.document.close();
        } else {
          const data = await response.json();
          console.log(data.message);
        }
      }
    } catch (error) {
      console.error("Error:", error);
    }
  };

  const handleCoverLetterClick = async (coverLetter) => {
    if (!coverLetter) {
      alert(
        t(
          "dynamicFormTable.coverLetterNotAvailable",
          "Cover letter not available"
        )
      );
      return;
    }

    try {
      const response = await axios.post(
        `${apiUrl}ai/search_resume/`,
        { resume_url: coverLetter },
        { responseType: "blob" }
      );

      if (response.headers["content-type"].includes("application/pdf")) {
        const blob = new Blob([response.data], { type: "application/pdf" });
        const url = window.URL.createObjectURL(blob);
        window.open(url, "_blank");
      } else {
        console.error(
          "Unexpected response type:",
          response.headers["content-type"]
        );
      }
    } catch (error) {
      console.error("Cover letter open error:", error);
      console.error("Error details:", error.response || error.message);
      alert(
        t(
          "dynamicFormTable.failedToOpenCoverLetter",
          "Failed to open cover letter. Please try again later."
        )
      );
    }
  };

  const [filtredPosts, setFiltredPosts] = useState([]);
  const [hasMorePosts, setHasMorePosts] = useState(true);
  const [isLoadingMorePosts, setIsLoadingMorePosts] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [currentApplication, setCurrentApplication] = useState(null);
  
  // Use the new hook for spontaneous posts when dialog is open
  const { posts: spontaneousPosts, loading: spontaneousLoading, loadPosts: loadSpontaneousPosts } = usePostListForSpontaneous(
    getUserIdFromAccessToken(),
    currentApplication?.id, // Pass submission ID for direct reference
    currentApplication?.cv ? processCV(currentApplication.cv) : null,
    currentApplication?.extracted_informations
  );

  // Use regular hook for initial load
  const showAllPosts = true;
  const { posts, loading, loadPosts } = usePostList(
    getUserIdFromAccessToken(),
    showAllPosts
  );

  useEffect(() => {
    if (!loading) {
      console.log("Fetched Posts all:", posts); // This is the full response object

      // Extract only the results
      const resultsOnly = Array.isArray(posts?.results) ? posts.results : [];
      console.log("Fetched Posts Only (Results):", resultsOnly);
      setFiltredPosts(resultsOnly);
      setHasMorePosts(!!posts?.next);
      setCurrentPage(1);
    }
  }, [posts, loading]);

  // Update posts when spontaneous posts are loaded
  useEffect(() => {
    if (!spontaneousLoading && spontaneousPosts && currentApplication) {
      console.log("Fetched Spontaneous Posts:", spontaneousPosts);
      const resultsOnly = Array.isArray(spontaneousPosts?.results) ? spontaneousPosts.results : [];
      console.log("Fetched Spontaneous Posts Only (Results):", resultsOnly);
      setFiltredPosts(resultsOnly);
      setHasMorePosts(!!spontaneousPosts?.next);
      setCurrentPage(1);
    }
  }, [spontaneousPosts, spontaneousLoading, currentApplication]);

  // Function to load more posts for infinite scrolling
  const loadMorePosts = async () => {
    if (isLoadingMorePosts || !hasMorePosts) return;
    
    setIsLoadingMorePosts(true);
    try {
      const userId = getUserIdFromAccessToken();
      const nextPage = currentPage + 1;
      
      // Use different endpoint based on whether we're loading for spontaneous assignment
      let endpoint;
      if (currentApplication) {
        // Use spontaneous endpoint
        const params = new URLSearchParams();
        params.append('page', nextPage);
        if (currentApplication.id) {
          params.append('submission_id', currentApplication.id);
        }
        if (currentApplication.cv) {
          params.append('resume_path', processCV(currentApplication.cv));
        }
        if (currentApplication.extracted_informations) {
          params.append('extracted_informations', JSON.stringify(currentApplication.extracted_informations));
        }
        endpoint = `${process.env.REACT_APP_API_URL}posts/list/${userId}/spontaneous/?${params.toString()}`;
      } else {
        // Use regular endpoint
        endpoint = `${process.env.REACT_APP_API_URL}posts/list/${userId}/?page=${nextPage}`;
      }
      
      const response = await axios.get(endpoint, {
        headers: {
          "Content-type": "application/json",
          Authorization: "Bearer " + localStorage.getItem("accessToken"),
        },
      });
      
      const newPosts = response.data.results || [];
      setFiltredPosts(prev => [...prev, ...newPosts]);
      setCurrentPage(nextPage);
      setHasMorePosts(!!response.data.next);
    } catch (error) {
      console.error('Error loading more posts:', error);
    } finally {
      setIsLoadingMorePosts(false);
    }
  };

  const [openDialog, setOpenDialog] = useState(false);

  const handleOpenDialog = (application) => {
    setCurrentApplication(application);
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
  };

  const handleEmailClick = () => {
    fetchTemplates();
    setIsTemplateModalOpen(true);
  };

  const toggleAdditionalFields = () => {
    setShowAdditionalFields(!showAdditionalFields);
  };

  const parseStringToArray = (data) => {
    if (!data) return [];

    // If it's already an array, just return it
    if (Array.isArray(data)) return data;

    // If it's a string, try to parse it
    if (typeof data === "string") {
      try {
        // First try to parse as JSON
        return JSON.parse(data);
      } catch (e) {
        // If JSON parsing fails, try to clean and split by commas
        const cleanedString = data
          .replace(/^\[|\]$|'|"/g, "") // Remove brackets, single and double quotes
          .split(",")
          .map((item) => item.trim())
          .filter(Boolean); // Remove empty strings

        return cleanedString;
      }
    }
    return [];
  };

// Function to render limited items with "Show More" button (only for Champs Extra)
 // Fixed renderLimitedItems function - replace the existing one
const renderLimitedItems = (items, title, limit = 5, charLimit = 9) => { 
  const processedItems = parseStringToArray(items); 

  if (!processedItems || processedItems.length === 0) { 
    return ( 
      <Typography 
        variant="body2" 
        color="textSecondary" 
        sx={{ fontSize: "1.1rem" }} 
      > 
        {t("dynamicFormTable.noItemsListed", "No {{title}} listed", { 
          title: title.toLowerCase(), 
        })} 
      </Typography> 
    ); 
  } 

  // Use the charLimit parameter instead of responsive limits
  const characterLimit = charLimit || 9; 

  // Helper function to check if any item needs truncation 
  const needsTruncation = (item) => 
    cleanString(item).length > characterLimit; 
  const hasLongContent = processedItems.some(needsTruncation); 

  // Only show button for "Champs Extra"
  const showButton = title === "Champs Extra" && (processedItems.length > limit || hasLongContent); 

  return ( 
    <Box 
      sx={{ 
        display: "flex", 
        alignItems: "center", 
        flexDirection: "row", 
        gap: 0, 
        width: "100%", 
        minWidth: 0, 
      }} 
    >
      <Box 
        sx={{ 
          display: "flex", 
          flexWrap: "wrap", 
          flex: "1 1 auto", 
          minWidth: 0, 
          maxWidth: showButton ? "calc(100% - 35px)" : "100%", 
          gap: 0, 
        }} 
      > 
        {processedItems.slice(0, limit).map((item, i) => { 
          const cleanedItem = cleanString(item); 
          
          // Only truncate for "Champs Extra", show full text for other fields
          const displayText = title === "Champs Extra" && cleanedItem.length > characterLimit
            ? `${cleanedItem.substring(0, characterLimit)}...` 
            : cleanedItem; 

          // Calculate if this item was truncated
          const isTruncated = title === "Champs Extra" && cleanedItem.length > characterLimit;
          
          // FIX: For non-"Champs Extra" fields, always use fit-content width
          const isExtraField = title === "Champs Extra";

          return ( 
            <InfoChip 
              key={i} 
              label={displayText} 
              sx={{ 
                // MAIN FIX: Use fit-content for all non-Extra fields, and for truncated Extra fields
                width: (!isExtraField || isTruncated) ? "fit-content" : "auto",
                maxWidth: "100%", 
                fontSize: { xs: "0.875rem", sm: "1rem" }, 
                backgroundColor: "rgba(0, 23, 231, 0.08)", 
                color: "#0017E7", 
                border: "1px solid rgba(0, 23, 231, 0.15)", 
                fontWeight: 500, 
                margin: 0, 
                marginRight: "2px", 
                "& .MuiChip-label": { 
                  fontSize: { xs: "0.875rem", sm: "1rem" }, 
                  fontWeight: 500, 
                  overflow: "hidden", 
                  textOverflow: "ellipsis", 
                  padding: { xs: "4px 8px", sm: "6px 12px" },
                  // Ensure proper text wrapping for non-Extra fields
                  whiteSpace: isExtraField ? "nowrap" : "normal",
                  maxWidth: "100%",
                }, 
              }} 
            /> 
          ); 
        })} 
      </Box> 

      {showButton && ( 
        <ShowMoreButton 
          onClick={() => handleShowMore(title, processedItems)} 
          size="small" 
          sx={{ 
            ...showMoreInlineSx, 
            minWidth: "auto", 
            whiteSpace: "nowrap", 
            padding: "4px 6px", 
            margin: 0, 
            marginLeft: "4px", 
            fontSize: { xs: "0.75rem", sm: "0.875rem" }, 
            height: "auto", 
            lineHeight: 1, 
            display: "flex", 
            alignItems: "center", 
            flexShrink: 0, 
          }} 
        > 
          {processedItems.length > limit 
            ? `+${processedItems.length - limit}` 
            : "plus"} 
        </ShowMoreButton> 
      )} 
    </Box> 
  ); 
};
  const handleShowMore = (title, items) => {
    setMoreItemsDialogTitle(title);
    setMoreItemsDialogContent(items);
    setMoreItemsDialogOpen(true);
  };

  // Education data
  const educationData = application.form_data.educationLevel
    ? [application.form_data.educationLevel]
    : [];

  // Skills data (could be extracted from dynamicFields)
  const skillsData = dynamicFields
    .filter((field) => field.label.toLowerCase().includes("skill"))
    .map((field) => field.value);

  // Experience data
  const experienceData = application.form_data.experienceYears
    ? [
        `${application.form_data.experienceYears} ${t(
          "dynamicFormTable.yearsOfExperience",
          "years of experience"
        )}`,
      ]
    : [];

  return (
    <>
      <StyledCard
        sx={{
          boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
          borderRadius: "12px",
          border: "1px solid #e0e0e0",
          transition: "all 0.3s ease",
          "&:hover": {
            boxShadow: "0 8px 24px rgba(0,0,0,0.15)",
            transform: "translateY(-2px)",
          },
        }}
      >
        <CardContent
          className="p-4"
          sx={{
            pt: { xs: 1, sm: 1.5, md: 2 },
            pb: { xs: 1, sm: 1.5, md: 2 },
            px: { xs: 1.5, sm: 2, md: 2.5 },
            "&:last-child": { pb: 1 },
          }}
        >
          <Grid container spacing={1} alignItems="flex-start">
            {/* Column 1: Name, Profile, Contact Info */}
            <Grid item xs={12} sm={6} md={3} lg={3}>
              <Box className="flex flex-col gap-1 mb-1">
                {/* Name and Profile Image Section */}
                <Box
                  sx={{
                    mb: 1,
                    display: "flex",
                    alignItems: "flex-start",
                    gap: 2,
                  }}
                >
                  {/* Profile Image */}
                  <Avatar
                    src={
                      // First check if there's a direct image URL in form_data
                      application.form_data.image &&
                      application.form_data.image !== "null" &&
                      application.form_data.image !== null &&
                      application.form_data.image.startsWith("http")
                        ? application.form_data.image
                        : // Then check for application.image with API URL
                        application.image &&
                          application.image !== "null" &&
                          application.image !== null
                        ? `${apiUrl}${application.image}`
                        : undefined
                    }
                    alt={`${application.form_data.firstName} ${application.form_data.lastName}`}
                    sx={{
                      width: { xs: 50, sm: 60, md: 80 },
                      height: { xs: 50, sm: 60, md: 80 },
                      border: "2px solid #e0e0e0",
                      backgroundColor: "#e0e0e0",
                      color: "#666",
                      fontSize: { xs: "1rem", sm: "1.1rem", md: "1.2rem" },
                      fontWeight: 500,
                    }}
                  >
                    {application.form_data.firstName?.charAt(0)}
                    {application.form_data.lastName?.charAt(0)}
                  </Avatar>

                  {/* Name and Application Date */}
                  <Box sx={{ flex: 1 }}>
                    <Typography
                      variant="h5"
                      fontWeight="bold"
                      sx={{
                        fontSize: "1.5rem",
                        lineHeight: 1.2,
                        mb: 0.25,
                      }}
                    >
                      {application.form_data.firstName}{" "}
                      {application.form_data.lastName}
                    </Typography>

                    {/* Application Date - directly under name */}
                    {application?.submitted_at && (
                      <Typography
                        variant="body2"
                        sx={{
                          fontSize: "0.9rem",
                          fontWeight: 500,
                          display: "block",
                          mt: 0.25,
                          mb: 1,
                        }}
                      >
                        <Box component="span" sx={{ color: "#0017E7" }}>
                          {t("dynamicFormTable.applied", "Postulé:")}
                        </Box>
                        <Box
                          component="span"
                          sx={{ color: "#000000", ml: 0.5 }}
                        >
                          {new Date(
                            application.submitted_at
                          ).toLocaleDateString("fr-FR", {
                            day: "2-digit",
                            month: "short",
                            year: "numeric",
                          })}
                        </Box>
                      </Typography>
                    )}
                  </Box>
                </Box>

                {/* Contact Information Section - Replacing Date */}
                <Box sx={{ mb: 1 }}>
                  {/* <Typography
                    variant="h6"
                    fontWeight="bold"
                    sx={{
                      fontSize: "1.1rem",
                      mb: 2,
                      color: "#333",
                      display: "flex",
                      alignItems: "center",
                      gap: 1
                    }}
                  >
                    <EmailIcon sx={{ fontSize: "1.2rem" }} />
                    Contact
                  </Typography> */}
                  <Box
                    sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}
                  >
                    {application.form_data.email && (
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                          cursor: "pointer",
                          "&:hover": { color: "#1976d2" },
                        }}
                        onClick={() =>
                          (window.location.href = `mailto:${application.form_data.email}`)
                        }
                      >
                        <EmailIcon sx={{ fontSize: "1rem", color: "#666" }} />
                        <Typography
                          variant="body2"
                          sx={{
                            fontSize: "0.9rem",
                            wordBreak: "break-word",
                          }}
                        >
                          {application.form_data.email}
                        </Typography>
                      </Box>
                    )}
                    {application.form_data.linkedin && (
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                          cursor: "pointer",
                          "&:hover": { color: "#0077b5" },
                        }}
                        onClick={() =>
                          window.open(application.form_data.linkedin, "_blank")
                        }
                      >
                        <LinkedInIcon
                          sx={{ fontSize: "1rem", color: "#0077b5" }}
                        />
                        <Typography
                          variant="body2"
                          sx={{
                            fontSize: "0.9rem",
                            color: "#0077b5",
                            textDecoration: "underline",
                          }}
                        >
                          {t("dynamicFormTable.linkedInProfile", "LinkedIn Profile")}
                        </Typography>
                      </Box>
                    )}
                  </Box>
                </Box>

                {/* Profile Section */}
                {application.form_data.profile && (
                  <Box sx={{ mb: 3 }}>
                    <Typography
                      variant="h6"
                      fontWeight="bold"
                      sx={{
                        fontSize: "1.1rem",
                        mb: 1.5,
                        color: "#333",
                      }}
                    >
                      {t("dynamicFormTable.profile", "Profil")}
                    </Typography>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{
                        fontSize: "0.9rem",
                        display: "-webkit-box",
                        WebkitLineClamp: 3,
                        WebkitBoxOrient: "vertical",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        lineHeight: "1.5",
                        maxWidth: "100%",
                        backgroundColor: "#f8f9fa",
                        padding: "12px",
                        borderRadius: "8px",
                        border: "1px solid #e9ecef",
                      }}
                    >
                      {application.form_data.profile}
                    </Typography>
                  </Box>
                )}
              </Box>
            </Grid>

            {/* Column 2: Main Information Grid */}
            <Grid item xs={12} sm={6} md={6} lg={6}>
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: {
                    xs: "1fr", // Single column on mobile
                    sm: "1fr 1fr", // Two columns on tablet
                    md: "1fr 1fr 1fr", // Three columns on desktop
                  },
                  gridTemplateRows: "auto auto", // 2 rows
                  gap: { xs: 0.25, sm: 0.5, md: 0.75 },
                  mt: 0.5,
                  width: "100%",
                  minWidth: "100%", // Ensure full width usage
                }}
              >
                {/* Formation - Column 1, Row 1 */}
                {educationData && educationData.length > 0 && (
                  <Box
                    sx={{
                      gridColumn: { xs: "1", sm: "1", md: "1" },
                      gridRow: { xs: "1", sm: "1", md: "1" },
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "flex-start",
                      gap: { xs: 0.1, sm: 0.2, md: 0.3 },
                      width: "100%", // Fill grid cell width
                      minWidth: 0, // Prevent overflow
                    }}
                  >
                    {/* Formation Field */}
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "flex-start",
                      }}
                    >
                      <Typography
                        variant="h6"
                        className="mb-1 font-semibold"
                        fontWeight="bold"
                        sx={{
                          fontSize: "1.1rem",
                          mb: 0.25,
                          display: "flex",
                          alignItems: "center",
                        }}
                      >
                        <SchoolIcon sx={{ mr: 1, fontSize: "1.5rem" }} />
                        {t("dynamicFormTable.education", "Formation")}
                      </Typography>
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: { xs: "flex-start", sm: "center" },
                          gap: 0,
                          width: "100%",
                          minWidth: 0,
                        }}
                      >
                        {renderLimitedItems(educationData, "Formation", 5, 9)}
                      </Box>
                    </Box>

                    {/* Expérience - Column 1, Row 2 */}
                    <Box
                      sx={{
                        gridColumn: { xs: "1", sm: "2", md: "1" },
                        gridRow: { xs: "2", sm: "1", md: "2" },
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "flex-start",
                        gap: { xs: 0.1, sm: 0.2, md: 0.3 },
                        width: "100%",
                        minWidth: 0,
                        mt: 0.5,
                      }}
                    >
                      <Typography
                        variant="h6"
                        className="mb-1 font-semibold"
                        fontWeight="bold"
                        sx={{
                          fontSize: "1.1rem",
                          mb: 0.25,
                          display: "flex",
                          alignItems: "center",
                        }}
                      >
                        <WorkHistoryOutlined
                          sx={{ mr: 1, fontSize: "1.5rem" }}
                        />
                        {t("dynamicFormTable.experience", "Expérience")}
                      </Typography>
                      <InfoChip
                        label={`${
                          application.form_data.experienceYears || "0"
                        } ans`}
                        sx={{
                          fontSize: "1rem",
                          margin: "1px",
                          "& .MuiChip-label": {
                            fontSize: "1rem",
                            fontWeight: 500,
                            padding: "6px 12px",
                          },
                          backgroundColor: "rgba(0, 23, 231, 0.08)",
                          color: "#0017E7",
                          border: "1px solid rgba(0, 23, 231, 0.15)",
                          fontWeight: 500,
                        }}
                      />
                    </Box>
                  </Box>
                )}

                {/* Catégorie - Column 2, Row 1 */}
                {application.form_data.CategoryProfile && (
                  <Box
                    sx={{
                      gridColumn: { xs: "1", sm: "1", md: "2" },
                      gridRow: { xs: "3", sm: "2", md: "1" },
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "flex-start",
                      gap: { xs: 0.1, sm: 0.2, md: 0.3 },
                      width: "100%", // Fill grid cell width
                      minWidth: 0, // Prevent overflow
                    }}
                  >
                    {/* Category Field */}
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "flex-start",
                      }}
                    >
                      <Typography
                        variant="h6"
                        className="mb-1 font-semibold"
                        fontWeight="bold"
                        sx={{
                          fontSize: "1.1rem",
                          mb: 0.25,
                          display: "flex",
                          alignItems: "center",
                        }}
                      >
                        <WorkIcon sx={{ mr: 1, fontSize: "1.5rem" }} />
                        {t("dynamicFormTable.category", "Catégorie")}
                      </Typography>
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 0,
                          width: "100%",
                          minWidth: 0,
                        }}
                      >
                        <InfoChip
                          label={
                            application.category_info?.category_name ||
                            application.form_data.CategoryProfile ||
                            t("dynamicFormTable.notApplicable", "N/A")
                          }
                          sx={{
                            maxWidth: "100%",
                            fontSize: { xs: "0.875rem", sm: "1rem" },
                            "& .MuiChip-label": {
                              fontSize: { xs: "0.875rem", sm: "1rem" },
                              fontWeight: 500,
                              padding: { xs: "4px 8px", sm: "6px 12px" },
                            },
                            backgroundColor: "rgba(0, 23, 231, 0.08)",
                            color: "#0017E7",
                            border: "1px solid rgba(0, 23, 231, 0.15)",
                            fontWeight: 500,
                          }}
                        />
                      </Box>
                    </Box>

                    {/* Localisation - Column 2, Row 2 */}
                    <Box
                      sx={{
                        gridColumn: { xs: "1", sm: "2", md: "2" },
                        gridRow: { xs: "4", sm: "2", md: "2" },
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "flex-start",
                        gap: { xs: 0.5, sm: 0.75, md: 1 },
                        width: "100%", // Fill grid cell width
                        minWidth: 0, // Prevent overflow
                      }}
                    >
                      <Typography
                        variant="h6"
                        className="mb-1 font-semibold"
                        fontWeight="bold"
                        sx={{
                          fontSize: "1.1rem",
                          mb: 0.5,
                          display: "flex",
                          alignItems: "center",
                        }}
                      >
                        <LocationOnIcon sx={{ mr: 1, fontSize: "1.5rem" }} />
                        {t("dynamicFormTable.location", "Localisation")}
                      </Typography>
                      {renderLimitedItems(
                        [application.form_data.city || t("dynamicFormTable.notApplicable", "Non")],
                        "Localisation",
                        5,
                        9
                      )}
                    </Box>
                  </Box>
                )}

                {/* Column 3, Row 1 - Combined Sous-catégorie AND Champs Extra */}
                <Box
                  sx={{
                    gridColumn: { xs: "1", sm: "1", md: "3" },
                    gridRow: { xs: "5", sm: "3", md: "1" }, // Both sections in Row 1
                    display: "flex",
                    flexDirection: "column", // Stack vertically
                    alignItems: "flex-start",
                    gap: { xs: 1, sm: 1.5, md: 2 }, // Space between the two sections
                    width: "100%",
                    minWidth: 0,
                  }}
                >
                  {/* SOUS-CATÉGORIE Section */}
                  {application.form_data.SubCategoryProfile && (
                    <Box sx={{ width: "100%" }}>
                      <Typography
                        variant="h6"
                        className="mb-1 font-semibold"
                        fontWeight="bold"
                        sx={{
                          fontSize: "1.1rem",
                          mb: 0.5,
                          display: "flex",
                          alignItems: "center",
                        }}
                      >
                        <WorkIcon sx={{ mr: 1, fontSize: "1.5rem" }} />
                        {t("dynamicFormTable.subcategory", "Sous-catégorie")}
                      </Typography>
                      {renderLimitedItems(
                        [application.category_info?.subcategory_name || application.form_data.SubCategoryProfile || "N/A"],
                        "Sous-catégorie",
                        5,
                        9
                      )}
                    </Box>
                  )}

                  {/* CHAMPS EXTRA Section */}
                  {dynamicFields && dynamicFields.length > 0 && (
                    <Box sx={{ width: "100%" }}>
                      <Typography
                        variant="h6"
                        className="mb-1 font-semibold"
                        fontWeight="bold"
                        sx={{
                          fontSize: "1.1rem",
                          mb: 0.25,
                          display: "flex",
                          alignItems: "center",
                        }}
                      >
                        <InfoOutlinedIcon sx={{ mr: 1, fontSize: "1.5rem" }} />
                        {t("dynamicFormTable.extraFields", "Champs Extra")}
                      </Typography>
                                             {renderLimitedItems(
                         dynamicFields.map(f => `${f.label}: ${f.value}`),
                         "Champs Extra",
                         1,
                         25
                       )}
                    </Box>
                  )}
                </Box>
              </Box>
            </Grid>

            {/* Column 3: Action Buttons */}
            <Grid item xs={12} sm={6} md={3} lg={3}>
              <Box
                className="flex flex-col gap-1 mb-1"
                sx={{
                  justifyContent: "flex-start",
                  height: "100%",
                  width: "100%", // Use full column width
                  minWidth: 0, // Prevent overflow
                }}
              >
                {/* Skills */}
                {skillsData && skillsData.length > 0 && (
                  <Box sx={{ mb: 1 }}>
                    <Typography
                      variant="h6"
                      className="mb-1 font-semibold flex items-center"
                      fontWeight="bold"
                      sx={{ fontSize: "1.1rem" }}
                    >
                      <Checklist className="w-6 h-6" sx={{ mr: 1 }} />
                      {t("dynamicFormTable.skills", "Skills")}
                    </Typography>
                    {renderLimitedItems(
                      skillsData,
                      t("dynamicFormTable.skills", "Skills")
                    )}
                  </Box>
                )}

                {/* Action Buttons Section */}
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    gap: { xs: 0.5, sm: 0.75 },
                    mt: { xs: 1, sm: 2 },
                    width: "100%",
                  }}
                >
                  {/* Contact Actions Group */}
                  <Box sx={{ display: "flex", gap: 0.25, width: "100%" }}>
                    <OutlinedButton
                      variant="outlined"
                      startIcon={<MdAssignmentAdd />}
                      onClick={() => handleOpenDialog(application)} // Pass application to dialog
                      size={isMobile ? "small" : "medium"}
                      sx={{
                        flex: 1,
                        height: "auto",
                        py: 1,
                        minWidth: 140, // Add minimum width for this button
                        borderColor: "#495057",
                        color: "#495057",
                        fontWeight: 600,
                        "&:hover": {
                          backgroundColor: "#495057",
                          color: "white",
                        },
                      }}
                    >
                      {t("dynamicFormTable.assignToPost", "Affecter à l'offre")}
                    </OutlinedButton>
                    {/* Dialog Component */}
                    <PostAssignmentDialog
                      open={openDialog}
                      onClose={handleCloseDialog}
                      posts={filtredPosts} // Pass filtered posts
                      cv={currentApplication ? processCV(currentApplication.cv) : ''}
                      extractedData={currentApplication?.extracted_informations}
                      applicationId={currentApplication?.id}
                      setRefreshcandidates={setRefreshcandidates}
                      onLoadMorePosts={loadMorePosts}
                      hasMorePosts={hasMorePosts}
                      isLoadingMore={isLoadingMorePosts}
                    />
                  </Box>

                  <Box sx={{ display: "flex", gap: 0.25, width: "100%" }}>
                    <OutlinedButton
                      startIcon={<HistoryIcon />}
                      onClick={handleEmailHistoryClick}
                      size={isMobile ? "small" : "medium"}
                      sx={{
                        flex: 1,
                        height: "auto",
                        py: 0.5,
                        minWidth: 50,
                        fontSize: { xs: "0.7rem", sm: "0.8rem" },
                      }}
                    >
                      {t("dynamicFormTable.history", "HISTORIQUE")}
                    </OutlinedButton>
                    <OutlinedButton
                      startIcon={<EmailIcon />}
                      onClick={handleEmailClick}
                      size={isMobile ? "small" : "medium"}
                      sx={{
                        flex: 1,
                        height: "auto",
                        py: { xs: 0.75, sm: 0.5 },
                        px: { xs: 1, sm: 1.5 },
                        minWidth: { xs: 100, sm: 120 },
                        fontSize: { xs: "0.75rem", sm: "0.875rem" },
                        borderColor: "#4CAF50",
                        color: "#4CAF50",
                        border: "1px solid #4CAF50",
                        "&:hover": {
                          backgroundColor: "rgba(76, 175, 80, 0.1)",
                          borderColor: "#4CAF50",
                        },
                      }}
                    >
                      {t("applicationTable.contact", "CONTACT")}
                    </OutlinedButton>
                  </Box>

                  {/* Documents Group */}
                  <Box sx={{ display: "flex", gap: 0.25, width: "100%" }}>
                    <OutlinedButton
                      startIcon={<PdfIcon />}
                      onClick={handleResumeClick}
                      size={isMobile ? "small" : "medium"}
                      sx={{
                        flex: 1,
                        height: "auto",
                        py: 0.5,
                        minWidth: 100, // Add minimum width
                      }}
                    >
                      {t("dynamicFormTable.cv", "CV")}
                    </OutlinedButton>
                    <OutlinedButton
                      startIcon={<BookIcon />}
                      onClick={() =>
                        handleCoverLetterClick(application.cover_letter)
                      }
                      size={isMobile ? "small" : "medium"}
                      sx={{
                        flex: 1,
                        height: "auto",
                        py: 0.5,
                        minWidth: 100, // Add minimum width
                      }}
                    >
                      {t("dynamicFormTable.coverLetter", "LETTRE")}
                    </OutlinedButton>
                  </Box>

                  {/* Date Display - Bottom Right */}
                </Box>
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </StyledCard>

      {/* "Show More" Dialog */}
      <Dialog
        open={moreItemsDialogOpen}
        onClose={() => setMoreItemsDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle
          sx={{ bgcolor: "#f5f5f5", borderBottom: "1px solid #e0e0e0" }}
        >
          <Typography
            variant="h5"
            component="div"
            sx={{ fontWeight: 600, color: "#333" }}
          >
            {moreItemsDialogTitle}
          </Typography>
          <IconButton
            aria-label="close"
            onClick={() => setMoreItemsDialogOpen(false)}
            sx={{
              position: "absolute",
              right: 8,
              top: 8,
              color: "#666",
            }}
          >
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers sx={{ padding: 0 }}>
          {moreItemsDialogTitle === "Experience" ? (
            <Box sx={{ maxHeight: "70vh", overflow: "auto", padding: 3 }}>
              {moreItemsDialogContent.map((experience, index) => (
                <Box
                  key={index}
                  sx={{
                    mb: 3,
                    p: 2.5,
                    borderRadius: 2,
                    border: "1px solid #e0e0e0",
                    boxShadow: "0 2px 4px rgba(0,0,0,0.05)",
                    backgroundColor: index % 2 === 0 ? "#f9fafb" : "#ffffff",
                  }}
                >
                  <Typography
                    variant="body1"
                    component="div"
                    sx={{
                      color: "#424242",
                      fontSize: "1rem",
                      fontWeight: 500,
                      lineHeight: 1.6,
                      whiteSpace: "pre-line",
                    }}
                  >
                    {experience.text}
                  </Typography>

                  {experience.date_range && (
                    <Typography
                      variant="body2"
                      component="div"
                      sx={{
                        color: "#757575",
                        fontSize: "0.875rem",
                        fontStyle: "italic",
                        mt: 1,
                      }}
                    >
                      {experience.date_range}
                    </Typography>
                  )}
                </Box>
              ))}
            </Box>
          ) : (
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.25, p: 2 }}>
              {moreItemsDialogContent.map((item, i) => {
                if (typeof item === "object" && item.type === "link") {
                  return (
                    <InfoChip
                      key={i}
                      label={item.label}
                      component="a"
                      href={item.href}
                      target="_blank"
                      clickable
                      sx={{
                        margin: "4px",
                        "& .MuiChip-label": {
                          whiteSpace: "normal",
                          wordBreak: "break-word",
                          padding: "12px 16px",
                          fontSize: "1rem",
                          lineHeight: "1.5",
                        },
                      }}
                    />
                  );
                }
                return (
                  <InfoChip
                    key={i}
                    label={cleanString(item)}
                    sx={{
                      margin: "4px",
                      "& .MuiChip-label": {
                        whiteSpace: "normal",
                        wordBreak: "break-word",
                        padding: "12px 16px",
                        fontSize: "1rem",
                        lineHeight: "1.5",
                      },
                    }}
                  />
                );
              })}
            </Box>
          )}
        </DialogContent>
        <DialogActions
          sx={{
            padding: 2,
            borderTop: "1px solid #e0e0e0",
            justifyContent: "flex-end",
          }}
        >
          <Button
            onClick={() => setMoreItemsDialogOpen(false)}
            color="primary"
            variant="contained"
            sx={{
              px: 3,
              py: 1,
              textTransform: "none",
              fontWeight: 500,
            }}
          >
            {t("dynamicFormTable.close", "Close")}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Email Modals */}
      {isTemplateModalOpen && (
        <SendEmailModal
          open={isTemplateModalOpen}
          onClose={() => setIsTemplateModalOpen(false)}
          recruiterId={userId}
          theme={theme}
          candidateInfo={{
            email: application.form_data.email,
            firstName: application.form_data.firstName || "",
            lastName: application.form_data.lastName || "",
            company: company,
          }}
          showTemplateSelection={true}
        />
      )}

      {isEmailHistoryOpen && (
        <EmailHistory
          open={isEmailHistoryOpen}
          onClose={() => setIsEmailHistoryOpen(false)}
          candidateEmail={application.form_data.email}
          recruiterId={userId}
          theme={theme}
        />
      )}
    </>
  );
};

const ViewToggleButton = styled(Button)(({ theme }) => ({
  borderRadius: "14px",
  textTransform: "none",
  fontWeight: 600,
  padding: "12px 24px",
  fontSize: "0.95rem",
  background: "linear-gradient(135deg, #0017E7 0%, rgba(0, 23, 231, 0.8) 100%)",
  color: "#ffffff",
  border: "none",
  boxShadow: "0 6px 20px rgba(0, 23, 231, 0.25)",
}));

const DynamicFormTable = ({
  data = [],
  viewType,
  setViewType,
  showShareButton = false,
  shareCompanyName = "",
  disableScaling = false,
  hidePagination = false,
  onNavigateToApplicationTable = null, // Navigation callback to ApplicationTable
  onNavigateToBlacklist = null, // Navigation callback to Blacklist
  initialTotalCount = null, // Total count from infinite scroll
  enableInfiniteScroll = false, // Whether infinite scroll is enabled
  infiniteScrollPageSize = 20, // Page size for infinite scroll
  fetchMoreData = null, // Function to fetch more data for infinite scroll
  onSearchResults = null, // Function to handle search results for infinite scroll
  infiniteScrollData = null, // Data from infinite scroll component
  infiniteScrollTotalCount = null, // Total count from infinite scroll component
}) => {
  const { t, i18n } = useTranslation();
  const [applications, setApplications] = useState([]);
  const [allApplications, setAllApplications] = useState([]); // Store all applications for pagination
  const [totalCount, setTotalCount] = useState(0); // Add state for total count
  const [formStructure, setFormStructure] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [adminId, setAdminId] = useState("");
  const [adminCompany, setAdminCompany] = useState("");
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "info",
  });
  const [scale, setScale] = useState(1);
  const initializationRef = useRef(false);

  // Pagination state (CustomPagination uses 0-based indexing)
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);


  // Add scaling effect (only if not disabled)
  useEffect(() => {
    if (disableScaling) {
      setScale(1); // Set scale to 1 (no scaling) if disabled
      return;
    }

    const handleResize = () => {
      const windowHeight = window.innerHeight;
      const windowWidth = window.innerWidth;
      const contentHeight = 800; // Approximate height of your content
      const contentWidth = 1200; // Approximate width of your content

      // Calculate scale based on both height and width
      const heightScale = (windowHeight * 0.95) / contentHeight; // 95% of window height
      const widthScale = (windowWidth * 0.95) / contentWidth; // 95% of window width

      // Use the smaller scale to ensure content fits both dimensions
      let newScale = Math.min(heightScale, widthScale, 1);

      // Add minimum scale limit based on screen width
      if (windowWidth <= 1920) {
        // For 1080p and similar resolutions
        newScale = Math.max(newScale, 0.8); // Minimum 80% scale
      } else if (windowWidth <= 2560) {
        // For 1440p
        newScale = Math.max(newScale, 0.85); // Minimum 85% scale
      } else {
        newScale = Math.max(newScale, 0.9); // Minimum 90% scale for larger screens
      }

      setScale(newScale);
    };

    handleResize(); // Initial scale
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [disableScaling]);

  const [refreshcandidates, setRefreshcandidates] = useState(false);
  useEffect(() => {
    if (refreshcandidates) {
      fetchApplications(adminId);
    }
  }, [refreshcandidates, adminId]);

  // Pagination helper functions
  const updatePagination = (allData) => {
    // Calculate start and end indices for current page
    const startIndex = page * rowsPerPage;
    const endIndex = startIndex + rowsPerPage;
    const paginatedData = allData.slice(startIndex, endIndex);

    setApplications(paginatedData);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0); // Reset to first page when changing rows per page
  };

  // Update pagination when allApplications, page, or rowsPerPage changes (only if pagination is not hidden)
  useEffect(() => {
    if (!hidePagination) {
      updatePagination(allApplications);
    }
  }, [allApplications, page, rowsPerPage, hidePagination]);

  useEffect(() => {
    console.log("🔍 DYNAMIC FORM INITIAL DEBUG - Setting applications with data:", data);

    // If infinite scroll is enabled, don't manage local state - let the infinite scroll component handle it
    if (enableInfiniteScroll) {
      console.log("🔍 DYNAMIC FORM INITIAL DEBUG - Infinite scroll enabled, skipping local state management");
      return;
    }

    // Prevent excessive re-initialization
    if (initializationRef.current && Array.isArray(data) && data.length === 0) {
      console.log("🔍 DYNAMIC FORM INITIAL DEBUG - Skipping re-initialization with empty data");
      return;
    }
    initializationRef.current = true;

    // Check if data is a paginated response from backend
    if (
      data &&
      typeof data === "object" &&
      data.results &&
      !Array.isArray(data)
    ) {
      // Handle paginated response format from backend
      const results = data.results;
      const count = data.count || results.length;

      if (hidePagination) {
        setApplications(results);
        setAllApplications(results);
        setTotalCount(count);
      } else {
        setAllApplications(results);
        setTotalCount(count);
      }
      console.log(
        `DynamicFormTable: Loaded ${results.length} applications from paginated response. Total: ${count}`
      );
    } else if (Array.isArray(data)) {
      // Handle direct array data
      if (hidePagination) {
        setApplications(data);
        setAllApplications(data);
        setTotalCount(data.length);
      } else {
        setAllApplications(data);
        setTotalCount(data.length);
      }
    }
  }, [data, hidePagination, enableInfiniteScroll]);

  // Debug: Log when applications state changes
  useEffect(() => {
    console.log("🔍 DYNAMIC FORM STATE DEBUG - applications state changed:", applications.slice(0, 2).map(a => ({ id: a.id, name: a.name || a.full_name })));
  }, [applications]);

  const fetchApplications = async (admin_id) => {
    try {
      setLoading(true);
      const recruiterId = getUserIdFromAccessToken();
      const response = await axios.get(
        `${apiUrl}candidates/api/dynamic-forms/${recruiterId}/`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("accessToken")}`,
          },
          params: { 
            page_size: 1000, // Get all records for initial load
          },
        }
      );

      // Handle paginated response from fetchApplications
      if (response.data && response.data.results) {
        // Paginated response
        const results = response.data.results;
        const count = response.data.count || results.length;

        if (hidePagination) {
          setApplications(results);
          setAllApplications(results);
          setTotalCount(count);
        } else {
          setAllApplications(results);
          setTotalCount(count);
          setPage(0);
        }
        console.log(
          `fetchApplications: Loaded ${results.length} applications from paginated response. Total: ${count}`
        );
      } else if (Array.isArray(response.data)) {
        // Direct array response (fallback)
        if (hidePagination) {
          setApplications(response.data);
          setAllApplications(response.data);
          setTotalCount(response.data.length);
        } else {
          setAllApplications(response.data);
          setTotalCount(response.data.length);
          setPage(0);
        }
      }
      setError(null);
    } catch (err) {
      console.error("Error fetching applications:", err);
      setAllApplications([]);
      setTotalCount(0);
      setError(err.response?.data?.message || "Failed to load applications");
      showSnackbar(
        "Failed to load applications: " + (err.message || "Unknown error"),
        "error"
      );
    } finally {
      setLoading(false);
    }
  };

  const fetchApplicationForm = async (admin_id) => {
    try {
      setLoading(true);
      const response = await axios.get(
        `${apiUrl}authentication/get_application_form_by_admin/${admin_id}/`
      );

      if (response.data && response.data.form_structure) {
        setFormStructure(response.data.form_structure);
        console.log("Form structure:", response.data.form_structure);
      } else if (Array.isArray(response.data)) {
        const firstForm = response.data[0];
        if (firstForm && firstForm.form_structure) {
          setFormStructure(firstForm.form_structure);
          console.log("Form structure from array:", firstForm.form_structure);
        }
      } else {
        console.log("Application form data:", response.data);
      }
    } catch (err) {
      console.error("Error fetching application form:", err);
    } finally {
      setLoading(false);
    }
  };

  const fetchAdminId = async () => {
    try {
      setLoading(true);
      const response = await axios.get(
        `${apiUrl}authentication/recruiter/${getUserIdFromAccessToken()}/admin/`
      );
      setAdminId(response.data.admin_id);
      setAdminCompany(response.data.admin_company);
      console.log("Admin company:", response.data.admin_company);
      setError(null);
      return response.data.admin_id;
    } catch (err) {
      console.error("Error fetching admin ID:", err);
      setError(err.response?.data?.message || "Failed to load admin ID");
      showSnackbar("Failed to load admin ID", "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const initFetch = async () => {
      const fetchedAdminId = await fetchAdminId();
      if (fetchedAdminId) {
        fetchApplicationForm(fetchedAdminId);
        fetchApplications(fetchedAdminId);
      }
    };
    initFetch();
  }, []);

  const showSnackbar = (message, severity = "info") => {
    setSnackbar({ open: true, message, severity });
  };

  const handleSearch = (searchResults, totalCount = null, searchParams = {}) => {
    const currentScrollPosition = window.pageYOffset || document.documentElement.scrollTop || document.body.scrollTop || 0;

    console.log("🔍 DYNAMIC FORM SEARCH DEBUG - handleSearch called with:", {
      searchResults: Array.isArray(searchResults) ? `Array(${searchResults.length})` : searchResults,
      totalCount: totalCount,
      searchParams: searchParams,
      enableInfiniteScroll: enableInfiniteScroll,
      onSearchResults: !!onSearchResults,
      currentScrollPosition: currentScrollPosition,
      timestamp: new Date().toISOString()
    });

    // If infinite scroll is enabled and onSearchResults is provided, delegate to it
    if (enableInfiniteScroll && onSearchResults) {
      console.log("🔍 DYNAMIC FORM SEARCH DEBUG - Delegating to infinite scroll handler, current scroll:", currentScrollPosition);
      onSearchResults(searchResults, totalCount, searchParams);
      return;
    } else {
      console.log("🔍 DYNAMIC FORM SEARCH DEBUG - NOT delegating to infinite scroll:", {
        enableInfiniteScroll: enableInfiniteScroll,
        onSearchResults: !!onSearchResults,
        willUpdateLocalState: true
      });
    }

    if (searchResults === null) {
      console.log("🔍 DYNAMIC FORM SEARCH DEBUG - Clear button clicked, resetting...");
      if (enableInfiniteScroll && onSearchResults) {
        // For infinite scroll, delegate the reset to the parent
        onSearchResults(null, 0, {});
      } else {
        // For regular mode, fetch original data
        fetchApplications(adminId);
      }
    } else {
      console.log("🔍 DYNAMIC FORM SEARCH DEBUG - Processing search results...");
      console.log("🔍 DYNAMIC FORM SEARCH DEBUG - Search results length:", searchResults.length);
      console.log("🔍 DYNAMIC FORM SEARCH DEBUG - First few results:", searchResults.slice(0, 3).map(r => ({ id: r.id, name: r.name || r.full_name })));

      // For infinite scroll mode, don't update local state directly
      // The parent infinite scroll component will handle the data updates
      if (!enableInfiniteScroll) {
        if (hidePagination) {
          // When pagination is hidden, set both applications and allApplications
          setApplications(searchResults);
          setAllApplications(searchResults);
          setTotalCount(totalCount !== null ? totalCount : searchResults.length);
        } else {
          // When pagination is enabled, store all data and reset page
          setAllApplications(searchResults);
          setTotalCount(totalCount !== null ? totalCount : searchResults.length);
          setPage(0); // Reset to first page when searching
        }
      }
    }
  };

  // Determine the data source: use infinite scroll data if available, otherwise use local state
  const applicationsToRender = enableInfiniteScroll && infiniteScrollData ? infiniteScrollData : applications;
  const totalCountToShow = enableInfiniteScroll && infiniteScrollTotalCount !== null ? infiniteScrollTotalCount : totalCount;

  console.log("🔍 DYNAMIC FORM DATA SOURCE DEBUG:", {
    enableInfiniteScroll: enableInfiniteScroll,
    infiniteScrollDataLength: infiniteScrollData ? infiniteScrollData.length : 0,
    localApplicationsLength: applications.length,
    usingInfiniteScrollData: enableInfiniteScroll && infiniteScrollData,
    applicationsToRenderLength: applicationsToRender.length,
    localTotalCount: totalCount,
    infiniteScrollTotalCount: infiniteScrollTotalCount,
    totalCountToShow: totalCountToShow
  });

  return (
    <Box
      sx={{
        zoom: scale,
        transformOrigin: "top left",
        transition: "zoom 0.3s ease-in-out",
        overflowX: "hidden",
        minHeight: "fit-content",
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mb: 2 }}>
        <Box sx={{ flex: 1 }}>
          <DynamicFormSearchBar
            data={applicationsToRender}
            onSearch={handleSearch}
            SEARCH_API_ENDPOINT={`${apiUrl}candidates/api/search_dynamic_form_submissions/`}
            AUTOCOMPLETE_API_ENDPOINT={`${apiUrl}candidates/api/dynamicform_autocomplete_suggestions/`}
            showShareButton={showShareButton}
            adminCompany={shareCompanyName || adminCompany}
            resultCount={totalCountToShow}
            onNavigateToApplicationTable={onNavigateToApplicationTable}
            onNavigateToBlacklist={onNavigateToBlacklist}
          />
        </Box>
      </Box>

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", p: 3 }}>
          <CircularProgress />
        </Box>
      ) : applicationsToRender.length === 0 ? (
        <Box
          sx={{
            textAlign: "center",
            py: 5,
            backgroundColor: "#f5f5f5",
            borderRadius: 2,
            my: 3,
          }}
        >
          <Typography
            variant="h6"
            color="textSecondary"
            sx={{ fontSize: "1.3rem" }}
          >
            {t(
              "dynamicFormTable.noApplicationsFound",
              "No applications found matching your search criteria"
            )}
          </Typography>
          <Typography
            variant="body2"
            color="textSecondary"
            sx={{ mt: 1, fontSize: "1.1rem" }}
          >
            {t(
              "dynamicFormTable.tryAdjustingFilters",
              "Try adjusting your search filters or clear them to see all applications"
            )}
          </Typography>
        </Box>
      ) : (
        <>
          <Box
            sx={{
              width: "100%",
              minHeight: "fit-content",
              overflow: "visible",
            }}
          >
            {viewType === "card" ? (
              // Card view
              <Grid
                container
                spacing={3}
                sx={{
                  width: "100%",
                  margin: 0,
                  "& .MuiGrid-item": {
                    paddingLeft: "24px",
                    paddingTop: "24px",
                  },
                }}
              >
                {applicationsToRender.map((application, index) => {
                  // Add error handling for malformed data
                  if (!application || !application.id) {
                    console.warn("Malformed application data:", application);
                    return null;
                  }

                  // Generate stable key using application ID
                  const stableKey = application.id || `dynamic-form-${index}`;

                  // Debug logging for first few items
                  if (index < 3) {
                    console.log("🔍 DYNAMIC FORM RENDER DEBUG - Application", index, ":", {
                      id: application.id,
                      stableKey: stableKey,
                      name: application.name || application.full_name,
                      totalRendered: applicationsToRender.length
                    });
                  }

                  return (
                    <Grid item xs={12} key={stableKey}>
                      <ApplicationCard
                        application={application}
                        company={application.company || adminCompany}
                        userId={getUserIdFromAccessToken()}
                        formStructure={formStructure}
                        setRefreshcandidates={setRefreshcandidates}
                      />
                    </Grid>
                  );
                })}
              </Grid>
            ) : (
              // List view
              <ApplicationTable2
                data={applications}
                viewType={viewType}
                setViewType={setViewType}
              />
            )}

          </Box>
          {/* CustomPagination Component */}
          {!hidePagination && allApplications.length > 0 && (
            <Box
              sx={{
                ".MuiTablePagination-selectLabel, .MuiTablePagination-displayedRows":
                {
                  m: 0,
                },
                ".MuiTablePagination-toolbar": {
                  flexWrap: "wrap",
                  gap: 1,
                  justifyContent: "center",
                  "@media (min-width: 600px)": {
                    justifyContent: "flex-end",
                  },
                },
              }}
            >
              <CustomPagination
                page={page}
                handleChangePage={handleChangePage}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                count={allApplications.length}
              />
            </Box>
          )}
        </>
      )}

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        sx={{
          "& .MuiSnackbarContent-root": {
            backgroundColor: "rgba(255, 255, 255, 0)",
            color: "black",
            borderRadius: "50px",
            backdropFilter: "blur(3px)",
            boxShadow: "0 4px 30px rgba(0, 0, 0, 0.5)",
          },
        }}
        message={snackbar.message}
      />
    </Box>
  );
};

export default React.memo(DynamicFormTable);